import { Message } from '@arco-design/web-react';
import type { FormInstance } from '@arco-design/web-react/es/Form';
import type { TFunction } from 'i18next';

import i18n from '@/i18n';

import { formatMaasFormError } from '../../../capabilities/maas-init';
import type { MaasSubmitError } from '../../../capabilities/maas-init/maasTypes';
import { MaasRunMode } from '../../../capabilities/maas-init/maasTypes';
import type { MaasGroupSpec } from '../../../capabilities/maas-init/maasTypes';
import { parseFormJsonField } from '../../../capabilities/maas-init/utils/jsonFormField';
import { readModelInstanceFormValues } from '../../../capabilities/maas-model-storage/modelFormUtils';
import { getModelSizeStorageMismatchErrorKey as getModelSizeMismatchFromFormUtils } from '../../../capabilities/maas-model-storage/modelFormUtils';
import type { ModelInstanceFormValues, ModelInstanceSpec } from '../../../capabilities/maas-model-storage/modelInstanceTypes';
import { normalizeModelSources } from '../../../capabilities/maas-model-storage/modelSources';
import {
  getDataVolumeStorageGi,
  getVolumeMountPath,
  isCreateDataVolume,
  isCreateDataVolumeConfigured,
  isExistingDataVolume,
} from '../../../capabilities/maas-model-storage/storageVolume';
import {
  collectBaselineNodesFromInitValues,
  validateMaasGroupedGpuUsageLatest,
} from '../../../capabilities/maas-node-groups/gpuResourceValidation';
import { getPrimaryDataVolumeClaimName, resolvePrimaryDataVolumeFromValues } from '../../../capabilities/maas-node-groups/masterGroupForm';
import type { EmbeddedNodeSpec } from '../../../capabilities/maas-node-groups/nodeFields';
import { parseNodesField } from '../../../capabilities/maas-node-groups/nodeFields';
import { validateGroupResourceForApi } from '../../../capabilities/maas-node-groups/submitValidation';
import { normalizeServiceList } from '../../../capabilities/service';
import { isMiGatewayModuleEnabled } from '../gatewayModule';
import { buildMiServiceMountNodeOptions, validateMiServiceMountGroupNames } from '../inferenceServiceMount';
import { SERVICE_MOUNT_GROUP_NAME_FIELD, normalizeServiceMountGroupNames } from '../serviceMountNode';
import {
  flattenModelInstanceInferenceForValidation,
  gatewayInferenceImageSatisfied,
  hasModelInstanceEmbeddedInferenceImage,
  validateModelInstanceEmbeddedSystemDisk,
  validateModelInstanceGroupedResources,
  validateModelInstanceInferenceTopology,
  validateModelInstanceKvOffload,
  validateModelInstanceUniqueNodeGroupNames,
} from './inferenceSubmitValidation';
import { serializeModelForApi } from './modelSource';

/** 从 innerForm / 直建 form 读取并校验全局节点组重名（与 getModelInstanceSubmitError 同源） */
export function getModelInstanceUniqueNodeGroupNamesError(form: FormInstance): MaasSubmitError | undefined {
  return validateModelInstanceUniqueNodeGroupNames(readModelInstanceFormValues(form));
}

/** SKU / 下单等：重名时 Message 并 throw（alreadyNotified） */
export function assertModelInstanceUniqueNodeGroupNamesOnForm(form: FormInstance, t: TFunction): void {
  const submitError = getModelInstanceUniqueNodeGroupNamesError(form);
  if (!submitError) return;
  const message = formatMaasFormError(t, submitError.key, submitError.fieldKey, submitError.params);
  Message.error(message);
  throw Object.assign(new Error(message), { alreadyNotified: true });
}

export function validateMaasStorage(values: ModelInstanceFormValues): boolean {
  const data = resolvePrimaryDataVolumeFromValues(values);
  if (!data) return false;
  if (!data.reclaimPolicy) return false;
  const mountPath = getVolumeMountPath(data) || values.model?.mountPath;
  if (!mountPath) return false;
  if (isExistingDataVolume(data)) return !!getPrimaryDataVolumeClaimName(values);
  if (isCreateDataVolume(data)) {
    return isCreateDataVolumeConfigured(data);
  }
  return false;
}

export function getModelSizeStorageMismatchErrorKey(values: ModelInstanceFormValues): string | undefined {
  return getModelSizeMismatchFromFormUtils(values);
}

/** 直建 / 提交前业务校验（含异步 GPU） */
export async function getModelInstanceSubmitError(
  values: ModelInstanceFormValues,
  spec: ModelInstanceSpec,
  options?: {
    initValues?: ModelInstanceFormValues;
    skipModelStorageValidation?: boolean;
    runtimeTypeOnly?: boolean;
    /** 复用节点组表单但不配置服务的业务页面（如训练集群）跳过服务校验。 */
    skipServiceValidation?: boolean;
  },
): Promise<MaasSubmitError | undefined> {
  if (!values.workspace?.trim()) {
    return { key: 'common:validate.required.select', fieldKey: 'common:tenant.space' };
  }
  if (!values.alias?.trim()) {
    return { key: 'common:validate.required', fieldKey: 'common:name' };
  }
  if (!values.runtime?.type || (!options?.runtimeTypeOnly && !values.runtime?.workload)) {
    return { key: 'common:validate.required.select', fieldKey: 'data-center:maas.runtime.type' };
  }

  if (!spec.framework) {
    return { key: 'common:validate.required', fieldKey: 'data-center:maas.inference.framework' };
  }
  if (!values.runMode) {
    return { key: 'common:validate.required', fieldKey: 'data-center:maas.deploy.mode' };
  }
  if (!options?.skipModelStorageValidation) {
    if (!validateMaasStorage(values)) {
      return { key: 'data-center:maas.storage.configure.first' };
    }
    const sources = normalizeModelSources(values.model);
    if (!sources.length || sources.some((entry) => !String(entry.name ?? '').trim() || !String(entry.size ?? '').trim())) {
      if (sources.some((entry) => !String(entry.name ?? '').trim())) {
        return { key: 'common:validate.required', fieldKey: 'data-center:maas.service.name' };
      }
      return { key: 'data-center:maas.model.size.required' };
    }
    if (!spec.model || !serializeModelForApi(spec.model)) {
      return { key: 'common:validate.required', fieldKey: 'data-center:maas.model.select' };
    }

    const sizeMismatchKey = getModelSizeStorageMismatchErrorKey(values);
    if (sizeMismatchKey) {
      return { key: sizeMismatchKey };
    }
  }

  const uniqueNodeNamesError = validateModelInstanceUniqueNodeGroupNames(values);
  if (uniqueNodeNamesError) return uniqueNodeNamesError;

  const topologyError = validateModelInstanceInferenceTopology(values, spec);
  if (topologyError) return topologyError;

  if (values._inferencePdSeparation === true && !isMiGatewayModuleEnabled(values)) {
    return { key: 'data-center:maas.gateway.required.pdSeparation', fieldKey: 'data-center:maas.gateway.node.config' };
  }

  const systemDiskError = validateModelInstanceEmbeddedSystemDisk(values);
  if (systemDiskError) return systemDiskError;

  if (!hasModelInstanceEmbeddedInferenceImage(values)) {
    return { key: 'data-center:maas.inference.image.required' };
  }

  if (!options?.skipServiceValidation) {
    const services = normalizeServiceList(parseFormJsonField(values._inferenceService));
    if (!services.length) {
      return {
        key: 'data-center:please.add.at.least.one',
        fieldKey: 'data-center:maas.inference.service.title',
      };
    }

    const mountOptions = buildMiServiceMountNodeOptions(values, i18n.t.bind(i18n));
    const mountGroupNames = normalizeServiceMountGroupNames((values as Record<string, unknown>)[SERVICE_MOUNT_GROUP_NAME_FIELD]);
    if (mountOptions.length && !mountGroupNames.length) {
      return { key: 'common:validate.required.select', fieldKey: 'data-center:maas.service.entry.nodes' };
    }
    const staleMountError = validateMiServiceMountGroupNames(values, i18n.t.bind(i18n));
    if (staleMountError) return staleMountError;
  }

  const replicas = Number(values.replicas);
  if (Number.isFinite(replicas) && replicas < 0) {
    return { key: 'data-center:maas.replicas.min', fieldKey: 'data-center:maas.replicas', params: { min: 0 } };
  }

  const resourceError = validateModelInstanceGroupedResources(spec);
  if (resourceError) return resourceError;

  const kvOffloadError = validateModelInstanceKvOffload(values, spec);
  if (kvOffloadError) return kvOffloadError;

  if (isMiGatewayModuleEnabled(values)) {
    if (!spec.gateway || !validateGroupResourceForApi(spec.gateway)) {
      return { key: 'data-center:maas.worker.group.resource.required' };
    }
    const gatewayNodes = parseNodesField(values.gatewayNodes) as EmbeddedNodeSpec[];
    if (!gatewayNodes.length || !gatewayNodes.every((node) => typeof node._systemDiskSize === 'number' && node._systemDiskSize > 0)) {
      return { key: 'data-center:please.add.at.least.one.system.disk', fieldKey: 'common:system.disk' };
    }
    if (!gatewayInferenceImageSatisfied(values, spec.gateway)) {
      return { key: 'data-center:maas.inference.image.required' };
    }
  }

  const baselineFromInit = options?.initValues ? collectBaselineNodesFromInitValues(options.initValues) : undefined;
  const kvBaseline = collectKvOffloadBaselineNodes(options?.initValues);
  const baselineNodes = [...(baselineFromInit ?? []), ...kvBaseline];
  return validateMaasGroupedGpuUsageLatest(flattenModelInstanceInferenceForValidation(spec), {
    baselineNodes: baselineNodes.length ? baselineNodes : undefined,
  });
}

/** 编辑态把缓存节点已占用 GPU 计入基线，避免与推理节点共用校验时被当成新增占用 */
function collectKvOffloadBaselineNodes(values?: ModelInstanceFormValues): MaasGroupSpec[] {
  if (!values?.kvOffloadRunMode && !values?.kvOffloadMasterNodes && !values?.kvOffloadWorkerNodes) return [];
  const masters = parseNodesField(values.kvOffloadMasterNodes) as MaasGroupSpec[];
  const workers = parseNodesField(values.kvOffloadWorkerNodes) as MaasGroupSpec[];
  if (values.kvOffloadRunMode === MaasRunMode.PrimarySecondary) return [...masters, ...workers];
  return workers;
}
